import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import './App.css';
import Register from './pages/auth/Register';
import Login from './pages/auth/Login';
import UploadDocument from './pages/documents/UploadDocument';
import DocumentList from './pages/documents/DocumentList';
import DocumentDetail from './pages/documents/DocumentDetail';
import { ProfilePage } from './pages/profile/ProfilePage';
import QuizList from './pages/quiz/QuizList';
import QuizCreation from './pages/quiz/QuizCreation';
import QuizEdit from './pages/quiz/QuizEdit';
import QuizTaking from './pages/quiz/QuizTaking';
import QuizResultsPage from './pages/quiz/QuizResultsPage';
import QuizHistory from './pages/quiz/QuizHistory';
import QuizAttempts from './components/quiz/QuizAttempts';
import UserManagement from './pages/admin/UserManagement';
import AIConfiguration from './pages/admin/AIConfiguration';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QuizProvider } from './contexts/QuizContext';
import Navbar from './components/layout/Navbar';

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Admin-only route component
const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, canManageUsers } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!canManageUsers()) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Document route component (admin and teacher only)
const DocumentRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, canViewDocuments } = useAuth();

  console.log('DocumentRoute - isAuthenticated:', isAuthenticated, 'canViewDocuments:', canViewDocuments());

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!canViewDocuments()) {
    console.log('User không có quyền xem documents, redirect to /quizzes');
    return <Navigate to="/quizzes" />;
  }

  return <>{children}</>;
};

// Quiz creation route component (admin and teacher only)
const QuizCreateRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, canCreateQuizzes } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!canCreateQuizzes()) {
    return <Navigate to="/quizzes" />;
  }

  return <>{children}</>;
};

// Quiz redirect component
const QuizRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/quiz/${id}/take`} replace />;
};

function AppRoutes() {
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/" element={
          <ProtectedRoute>
            <QuizList />
          </ProtectedRoute>
        } />
        <Route path="/documents/upload" element={
          <DocumentRoute>
            <UploadDocument />
          </DocumentRoute>
        } />
        <Route path="/documents" element={
          <DocumentRoute>
            <DocumentList />
          </DocumentRoute>
        } />
        <Route path="/documents/:id" element={
          <DocumentRoute>
            <DocumentDetail />
          </DocumentRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/quizzes" element={
          <ProtectedRoute>
            <QuizList />
          </ProtectedRoute>
        } />
        <Route path="/quiz/create" element={
          <QuizCreateRoute>
            <QuizCreation />
          </QuizCreateRoute>
        } />
        <Route path="/quiz/:id/edit" element={
          <QuizCreateRoute>
            <QuizEdit />
          </QuizCreateRoute>
        } />
        <Route path="/quiz/history" element={
          <ProtectedRoute>
            <QuizHistory />
          </ProtectedRoute>
        } />
        
        {/* Admin routes */}
        <Route path="/admin/users" element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        } />
        <Route path="/admin/ai-configuration" element={
          <AdminRoute>
            <AIConfiguration />
          </AdminRoute>
        } />
        
        {/* Quiz attempts route (quiz owner only - checked by API) */}
        <Route path="/quiz/:quizId/attempts" element={
          <ProtectedRoute>
            <QuizAttempts />
          </ProtectedRoute>
        } />
        
        {/* Public quiz taking routes - no authentication required */}
        <Route path="/quiz/:id" element={<QuizRedirect />} />
        <Route path="/quiz/:id/take" element={<QuizTaking />} />

        <Route path="/quiz/:quizId/results/:attemptId" element={<QuizResultsPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <QuizProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </QuizProvider>
    </AuthProvider>
  );
}

export default App;
